import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import TootiDialog from '../components/common/Dialog'
import ParrotMark from '../components/common/ParrotMark'
import { logoutDialog } from '../lib/alerts'
import { profileName } from '../lib/profile'
import { planLabel } from '../lib/subscriptions'
import { subscriptionStatus } from '../services/subscriptions'
import { useAuthStore } from '../store/authStore'

/**
 * The site's account hub (`/account`): where signing in on the website
 * lands (owner's rule, 2026-09-19 — the web login is for buying and
 * managing the subscription, the profile and support, never for the
 * practice screens, which are the app's). Four doors and a way out:
 * subscription (the paywall), autopay, edit profile, support; log out.
 * Practice itself is pointed at the download page.
 */

const CARD = 'site-card site-card-lift flex flex-col gap-3 rounded-3xl border-2 border-line bg-surface p-5 sm:p-6'

function Door({ to, title, body, tone }) {
  return (
    <Link to={to} className={CARD}>
      <span className={`size-11 rounded-[14px] ${tone}`} />
      <span className="font-display text-xl font-extrabold text-ink">{title}</span>
      <span className="text-[14.5px] leading-[1.8] text-ink-2">{body}</span>
    </Link>
  )
}

export default function Account() {
  const { t } = useTranslation('site')
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const [subscription, setSubscription] = useState(null)
  const [askLogout, setAskLogout] = useState(false)

  useEffect(() => {
    let cancelled = false
    subscriptionStatus()
      .then((status) => {
        if (!cancelled) setSubscription(status)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="mx-auto flex w-full max-w-[860px] flex-col gap-[30px] px-4 pb-[clamp(44px,8vw,72px)] pt-[clamp(30px,6vw,54px)] sm:px-5">
      <header className="flex flex-wrap items-center gap-4" style={{ animation: 't-rise .5s ease-out both' }}>
        <ParrotMark size={64} />
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="self-start rounded-full bg-brand-50 px-3.5 py-1.5 font-display text-sm font-extrabold text-brand-600">{t('account.badge')}</span>
          <h1 className="font-display text-[clamp(28px,6vw,40px)] font-extrabold tracking-[-1.2px]">{t('account.greeting', { name: profileName(user) })}</h1>
          {user?.phone && (
            <span dir="ltr" className="tnum text-start text-sm text-ink-2">
              {user.phone}
            </span>
          )}
        </div>
      </header>

      <section className="flex flex-wrap items-center justify-between gap-4 rounded-[26px] border-2 border-line bg-accent-tint p-5 shadow-[0_6px_0_var(--color-line)] sm:p-7">
        <div className="flex min-w-[220px] flex-col gap-1.5">
          <span className="font-display text-[21px] font-extrabold">{t('account.subscriptionTitle')}</span>
          <span className="text-[14.5px] leading-[1.8] text-ink-2">
            {subscription ? t('account.currentPlan', { plan: planLabel(subscription) }) : t('account.subscriptionBody')}
          </span>
        </div>
        <Link
          to="/paywall"
          className="site-btn inline-flex h-14 items-center justify-center rounded-2xl bg-brand-600 px-[26px] font-display text-[17px] font-extrabold text-white [--press:var(--color-brand-deep)] dark:text-slate-900"
        >
          {subscription?.active ? t('account.manage') : t('account.subscribe')}
        </Link>
      </section>

      <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(230px,100%),1fr))]">
        <Door to="/autopay" title={t('account.autopayTitle')} body={t('account.autopayBody')} tone="bg-sky shadow-[0_3px_0_var(--color-sky-deep)]" />
        <Door to="/profile/edit" title={t('account.profileTitle')} body={t('account.profileBody')} tone="bg-violet shadow-[0_3px_0_var(--color-violet-deep)]" />
        <Door to="/contact" title={t('account.supportTitle')} body={t('account.supportBody')} tone="bg-coral shadow-[0_3px_0_var(--color-coral-deep)]" />
      </div>

      <section className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-line bg-surface-2 p-5 sm:p-6">
        <div className="flex min-w-[220px] flex-col gap-1.5">
          <span className="font-display text-lg font-extrabold">{t('account.practiceTitle')}</span>
          <span className="text-[14.5px] leading-[1.8] text-ink-2">{t('account.practiceBody')}</span>
        </div>
        <Link to="/download" className="site-btn inline-flex h-12 items-center justify-center rounded-2xl bg-surface px-5 font-display text-base font-extrabold text-ink [--press:var(--color-line-2)]">
          {t('account.practiceCta')}
        </Link>
      </section>

      <button
        type="button"
        onClick={() => setAskLogout(true)}
        className="self-start font-display text-[15px] font-bold text-ink-2 hover:text-ink"
      >
        {t('account.logout')}
      </button>

      {askLogout && (
        <TootiDialog
          spec={logoutDialog()}
          onConfirm={() => {
            setAskLogout(false)
            signOut()
          }}
          onDismiss={() => setAskLogout(false)}
        />
      )}
    </div>
  )
}
